import React, { useCallback, useState, useRef, useContext } from 'react';
import { GoogleMap, useJsApiLoader, Polygon } from '@react-google-maps/api';

import { db } from '../Db';
import { ExpandableRowContext, MapBuilderContext } from './MapBuilder.context';
import {
	ZOOM,
	GOOGLE_MAPS_OPTIONS,
	GOOGLE_MAP_CONTAINER_STYLE,
	GOOGLE_MAP_POLYGON_OPTIONS,
	getDefaultPath,
	CENTER,
	saveRemotePlot
} from '../App.constants';
import { getPropertyTitle } from '../App.hooks';

export const ExpandableRow = ({ data }) => {
	const [mode, setMode] = useState(null);
	const { updatePlotPath } = useContext(MapBuilderContext);
	const [path, setPath] = useState(data?.path ? data.path : null);
	const onSave = ({ path, plot }) => {
		saveData({ path, plot })
			.then(() => {
				updatePlotPath({ plot, path });
			})
			.catch(console.error);
	};

	const onDeleteClick = () => {
		setPath(null);
	};

	const onAddClick = () => {
		setPath(getDefaultPath());
	};

	return (
		<ExpandableRowContext.Provider
			value={{
				plot: data,
				path,
				setPath,
				onSave,
				mode,
				setMode,
				onDeleteClick,
				onAddClick,
			}}
		>
			<section className="expandable-row">
				<DataComponent />
				<div>
					<div>
						<MapComponent />
					</div>
				</div>
			</section>
		</ExpandableRowContext.Provider>
	);
};

async function saveData({ path, plot }) {
	const response = await saveDataRemotely({ path, plot });
	await db.plots.update(plot.id, { path: response });
}

async function saveDataRemotely({ path, plot }) {
	const { id } = plot;
	return saveRemotePlot({ id, path });
}

function MapComponent() {
	// Define refs for Polygon instance and listeners
	const { path, setPath, mode } = useContext(ExpandableRowContext);

	const polygonRef = useRef(null);
	const listenersRef = useRef([]);

	const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_OPTIONS);

	const polygonOptions = {
		...GOOGLE_MAP_POLYGON_OPTIONS,
		draggable: mode === 'edit',
		editable: mode === 'edit',
	};

	const onEdit = useCallback(() => {
		if (polygonRef.current) {
			const nextPath = polygonRef.current
				.getPath()
				.getArray()
				.map((latLng) => {
					return { lat: latLng.lat(), lng: latLng.lng() };
				});
			setPath(nextPath);
		}
	}, [setPath]);

	// Bind refs to current Polygon and listeners
	const onLoad = useCallback(
		(polygon) => {
			polygonRef.current = polygon;
			const path = polygon.getPath();
			listenersRef.current.push(
				path.addListener('set_at', onEdit),
				path.addListener('insert_at', onEdit),
				path.addListener('remove_at', onEdit),
			);
		},
		[onEdit],
	);

	// Clean up refs
	const onUnmount = useCallback(() => {
		listenersRef.current.forEach((lis) => lis.remove());
		polygonRef.current = null;
	}, []);

	if (loadError) {
		return (
			<div className={'map-container error'}>
				Error loading the map please refresh the page
			</div>
		);
	}

	return isLoaded ? (
		<div className="map-container" style={{ position: 'relative' }}>
			<GoogleMap
				zoom={ZOOM}
				center={path ? findCenter(path) : CENTER}
				mapContainerStyle={GOOGLE_MAP_CONTAINER_STYLE}
			>
				{path ? (
					<Polygon
						path={path}
						options={polygonOptions}
						// Event used when manipulating and adding points
						onMouseUp={onEdit}
						// Event used when dragging the whole Polygon
						onDragEnd={onEdit}
						onLoad={onLoad}
						onUnmount={onUnmount}
					/>
				) : null}
			</GoogleMap>
		</div>
	) : null;
}

function DataComponent() {
	const { plot, path, onSave, mode, setMode } =
		useContext(ExpandableRowContext);
	const {
		plan,
		block,
		street,
		avenue,
		estate,
		property,
		lot,
		type,
		owner,
		condition,
		category,
		area,
		range,
		certification,
		areaCode,
		corrections,
		lang
	} = plot;
	const onSubmit = (e) => {
		e.preventDefault();
		onSave({ path, plot });
		setMode(null);
	};

	return (
		<div className="data-container">
			<form onSubmit={onSubmit}>
				<span>
					<h2>{lang === 'en' ? 'Information' : 'Información'}</h2>
					{mode === 'edit' ? <EditButtonBar /> : <ButtonBar />}
				</span>

				<div>
					<h3>{getPropertyTitle('plan', lang)}</h3>
					<p>{plan}</p>
				</div>
				<div>
					<h3>{getPropertyTitle('block', lang)}</h3>
					<p>{block}</p>
				</div>
				<div>
					<h3>{getPropertyTitle('street', lang)}</h3>
					<p>{street}</p>
				</div>
				<div>
					<h3>{getPropertyTitle('avenue', lang)}</h3>
					<p>{avenue}</p>
				</div>
				<div>
					<h3>{getPropertyTitle('corrections', lang)}</h3>
					<p>{corrections}</p>
				</div>
				{estate ? (
					<div>
						<h3>{getPropertyTitle('estate', lang)}</h3>
						<p>{estate}</p>
					</div>
				) : null}
				{property ? (
					<div>
						<h3>{getPropertyTitle('property', lang)}</h3>
						<p>{property}</p>
					</div>
				) : null}
				<div>
					<h3>{getPropertyTitle('lot', lang)}</h3>
					<p>{lot}</p>
				</div>
				<div>
					<h3>{getPropertyTitle('type', lang)}</h3>
					<p>{type}</p>
				</div>
				<div>
					<h3>{getPropertyTitle('owner', lang)}</h3>
					<p>{owner}</p>
				</div>
				<div>
					<h3>{getPropertyTitle('condition', lang)}</h3>
					<p>{condition}</p>
				</div>
				<div>
					<h3>{getPropertyTitle('category', lang)}</h3>
					<p>{category}</p>
				</div>
				{area ? (
					<div>
						<h3>{getPropertyTitle('area', lang)}</h3>
						<p>{area}</p>
					</div>
				) : null}

				<div>
					<h3>{getPropertyTitle('range', lang)}</h3>
					<p>{range}</p>
				</div>
				<div>
					<h3>{getPropertyTitle('certification', lang)}</h3>
					<p>{certification ? (lang === 'es' ? 'Si' : 'Yes') : 'No'}</p>
				</div>
				{areaCode ? (
					<div>
						<h3>{getPropertyTitle('areaCode', lang)}</h3>
						<p>{areaCode}</p>
					</div>
				) : null}
				<div className="actions"></div>
			</form>
		</div>
	);
}

function ButtonBar() {
	const { setMode } = useContext(ExpandableRowContext);
	return (
		<div className="button-bar">
			<button
				type="button"
				title="Edit"
				onClick={() => {
					setMode('edit');
				}}
			>
				<EditIcon />
			</button>
		</div>
	);
}

function EditButtonBar() {
	const { onDeleteClick, onAddClick, path } =
		useContext(ExpandableRowContext);
	return (
		<div className="button-bar">
			{!path ? (
				<button title="Add" type="button" onClick={onAddClick}>
					<AddIcon />
				</button>
			) : null}
			{path ? (
				<button title="Delete" type="button" onClick={onDeleteClick}>
					<TrashIcon />
				</button>
			) : null}

			<button title="Save">
				<SaveIcon />
			</button>
		</div>
	);
}

function findCenter(markers) {
	const lats = markers.map((m) => m.lat);
	const lngs = markers.map((m) => m.lng);
	return {
		lat: (Math.min(...lats) + Math.max(...lats)) / 2,
		lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
	};
}

/* ICONS */

function SaveIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
			<polyline points="17 21 17 13 7 13 7 21"></polyline>
			<polyline points="7 3 7 8 15 8"></polyline>
		</svg>
	);
}

function EditIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
			<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
		</svg>
	);
}

function TrashIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<polyline points="3 6 5 6 21 6"></polyline>
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
			<line x1="10" y1="11" x2="10" y2="17"></line>
			<line x1="14" y1="11" x2="14" y2="17"></line>
		</svg>
	);
}

function AddIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
			<line x1="12" y1="8" x2="12" y2="16"></line>
			<line x1="8" y1="12" x2="16" y2="12"></line>
		</svg>
	);
}
